import React, { useEffect, useState } from "react";
import axios from "axios";
import { Scale } from "lucide-react";
import { applyTheme, applyFavicon, faviconDataUrl, themeGradient } from "./theme";

const API = process.env.REACT_APP_BACKEND_URL;
let cache = null;
let logoVersion = Date.now();

export const DEFAULT_BRANDING = {
  court_name: "Unified Justice Court", court_type: "COUNTY", tagline: "justice case manager", has_logo: false,
  theme_preset: "indigo", theme_font: "Outfit", login_bg_mode: "image", login_bg_color: "#312e81", has_login_bg: false,
  seal_style: "light", seal_overlay: "none", favicon_mode: "palette",
};

export const brandingLogoUrl = () => `${API}/api/ujms/branding/logo?v=${logoVersion}`;
export const loginBgUrl = () => `${API}/api/ujms/branding/login-bg?v=${logoVersion}`;

export const faviconFor = (b) => (b.favicon_mode === "logo" && b.has_logo ? brandingLogoUrl() : faviconDataUrl(b.theme_preset));

export const applyBranding = (b) => { applyTheme(b); applyFavicon(faviconFor(b)); document.title = `CJMS · ${b.court_name}`; };

export const getBranding = async (force = false) => {
  if (cache && !force) return cache;
  const token = localStorage.getItem("odyssey_token");
  try { cache = { ...DEFAULT_BRANDING, ...(await axios.get(`${API}/api/ujms/branding`, token ? { headers: { Authorization: `Bearer ${token}` } } : {})).data }; } catch (e) { cache = DEFAULT_BRANDING; }
  applyBranding(cache);
  return cache;
};
export const clearBrandingCache = () => { cache = null; logoVersion = Date.now(); };

export function loginBackground(brand) {
  const b = brand || DEFAULT_BRANDING;
  if (b.login_bg_mode === "custom" && b.has_login_bg) return `url('${loginBgUrl()}') center / cover no-repeat`;
  if (b.login_bg_mode === "color") return b.login_bg_color || DEFAULT_BRANDING.login_bg_color;
  if (b.login_bg_mode === "gradient") return themeGradient(b.theme_preset);
  return "url('/login-bg.jpg') center / cover no-repeat";
}

export function useBranding() {
  const [brand, setBrand] = useState(cache || DEFAULT_BRANDING);
  useEffect(() => { getBranding().then(setBrand); }, []);
  return brand;
}

const SEAL_CLS = {
  light: "bg-white text-ujms-deep",
  solid: "text-white ring-1 ring-white/70",
  outline: "bg-white text-ujms-dark ring-2 ring-ujms",
};

export function BrandMark({ className = "w-6 h-6", iconClassName = "", brand: brandProp, testId }) {
  const live = useBranding();
  const brand = brandProp || live;
  if (brand.has_logo) {
    if (brand.seal_overlay === "tint") {
      const mask = `url(${brandingLogoUrl()}) center / contain no-repeat`;
      return <span className={`${className} inline-block bg-ujms`} style={{ WebkitMask: mask, mask }} role="img"
        aria-label={`${brand.court_name} logo`} data-testid={testId || "brand-logo-tinted"} />;
    }
    return <img src={brandingLogoUrl()} alt={`${brand.court_name} logo`} className={`${className} object-contain`} data-testid={testId || "brand-logo"} />;
  }
  const style = brand.seal_style === "solid" ? { background: themeGradient(brand.theme_preset) } : undefined;
  return (
    <span className={`${className} inline-flex items-center justify-center rounded-full ${SEAL_CLS[brand.seal_style] || SEAL_CLS.light}`}
      style={style} data-testid={testId || "brand-default-mark"} data-seal-style={brand.seal_style || "light"}>
      <Scale className={iconClassName || "w-[70%] h-[70%]"} />
    </span>
  );
}
